import { Maatregel } from "@/entities/vsm";
import { useMaatregelContext } from "@/features/vsm/maatregel-context";
import { useMaatregelVergelijking } from "@/features/vsm/vsm-vergelijking-context";
import { Button } from "@/shared/components";
import { gewijzigdMarkering } from "./gewijzigd-markering";

type MaatregelToelichtingProps = {
    maatregel: Maatregel;
    isBewerkbaar: boolean;
};

export function MaatregelToelichting({ maatregel, isBewerkbaar }: Readonly<MaatregelToelichtingProps>) {
    const { acceptToelichting, removeToelichting } = useMaatregelContext();
    const { opmerking, huidigeToelichting } = maatregel;
    const suggestie = Maatregel.pendingToelichtingSuggestie(maatregel);
    const { gewijzigdeVelden } = useMaatregelVergelijking(maatregel);
    const isGewijzigd = gewijzigdeVelden.includes("opmerking");

    if (!isBewerkbaar) {
        return <ToelichtingTekst toelichting={opmerking ?? ""} isGewijzigd={isGewijzigd} />;
    }

    if (suggestie) {
        return (
            <ToelichtingSuggestie
                bron={suggestie === "huidig" ? "Huidig" : "Spoorweb"}
                toelichting={(suggestie === "huidig" ? huidigeToelichting : opmerking) ?? ""}
                onAccept={() => acceptToelichting(maatregel.id)}
                onRemove={() => removeToelichting(maatregel.id)}
            />
        );
    }

    return (
        <div className="flex items-center gap-2">
            <ToelichtingTekst toelichting={opmerking ?? ""} isGewijzigd={isGewijzigd} />
            <Button
                iconOnly
                iconSize="xs"
                variant="tertiary"
                size="s"
                icon="close"
                title="Toelichting verwijderen"
                onClick={() => removeToelichting(maatregel.id)}
                data-testid="remove-toelichting"
            />
        </div>
    );
}

function ToelichtingTekst({ toelichting, isGewijzigd = false }: Readonly<{ toelichting: string; isGewijzigd?: boolean }>) {
    return (
        <p className="text-content-subtle text-s min-w-0 flex-1" data-testid="maatregel-toelichting">
            <span className={gewijzigdMarkering(isGewijzigd, "toelichting")} data-gewijzigd={isGewijzigd}>
                {toelichting}
            </span>
        </p>
    );
}

type ToelichtingSuggestieProps = {
    bron: "Huidig" | "Spoorweb";
    toelichting: string;
    onAccept: () => void;
    onRemove: () => void;
};

function ToelichtingSuggestie({ bron, toelichting, onAccept, onRemove }: Readonly<ToelichtingSuggestieProps>) {
    return (
        <div
            className="bg-emphasis-primary flex items-center gap-2 rounded-md py-1 pr-1 pl-2"
            data-testid="maatregel-toelichting-suggestie"
            data-bron={bron}
        >
            <p className="text-content-subtle text-s min-w-0 flex-1 italic">
                {bron}: &ldquo;{toelichting}&rdquo;
            </p>
            <Button
                iconOnly
                iconSize="xs"
                variant="tertiary"
                size="s"
                icon="check"
                title="Toelichting accepteren"
                onClick={onAccept}
                data-testid="accept-toelichting"
            />
            <Button
                iconOnly
                iconSize="xs"
                variant="tertiary"
                size="s"
                icon="close"
                title="Suggestie verwijderen"
                onClick={onRemove}
                data-testid="remove-toelichting"
            />
        </div>
    );
}
